import type { GetStaticProps, GetStaticPaths, NextPage } from 'next';
import type { SimplePage } from 'interfaces/content/articles/Post';
import { StaticPage } from 'pages/Static/StaticPage';
import getPageByURI from 'api/Page/getPageByURI';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { setPianoConfig } from 'services/Piano';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import getFooter from 'api/Footer/getFooter';
import setWellFranchisePageAdConfig from 'services/Ad/config/setWellFranchisePageAdConfig';
import type { HeaderNav } from 'interfaces/navigation/Header';
import { wellLogo } from 'icons/WellLogo';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import { type HeadData, setHeadData } from 'utils/metaDataUtils/headData';
import getMenu from 'api/Header/getMenu';
import { Microsites } from 'constants/constants';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import { PianoExperiencePageType } from '@/app_lib/types/piano/Paywall';

interface PageProps {
  page: SimplePage;
}

const StaticNextPage: NextPage<PageProps> = ({ page }: PageProps) => (
  <StaticPage
    page={page}
    presentedBy={undefined}
    subDomain={Microsites.Well}
  />
);

export default StaticNextPage;

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { slug } = context.params as { slug: string };
    const page: SimplePage = await getPageByURI(`franchise-list-page/${slug}`);
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_WELL', Microsites.Well, wellLogo),
      fallbackData.headerNav,
    );

    const headData: HeadData = safe(() => setHeadData(page), fallbackData.headData);
    const arcSpanPageConfig: ArcSpanPageConfig = safe(() => setarcSpanPageConfig(page), fallbackData.arcSpanPageConfig);
    const pianoPageConfig: PianoPageConfig = setPianoConfig({
      pageType: PianoExperiencePageType.Default,
      pageData: {},
    });
    const pageAdConfig = safe(() => setWellFranchisePageAdConfig(page), fallbackData.pageAdConfig);

    return {
      props: {
        arcSpanPageConfig,
        footer,
        headData: { ...headData, siteName: 'Fortune Well' },
        headerNav: menu,
        page,
        pageAdConfig,
        pianoPageConfig,
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = () => ({
  fallback: 'blocking',
  paths: [{ params: { slug: 'best-places-families-methodology-2022' } }],
});
